import { Calendar, CalendarEvent, CalendarOccurrence } from '@/models/calendar';
import { CalendarService } from '@/services/calendar';
import { errorCatch } from '../utils';
// import dateFormat from '@/utils/dateFormat';
import { isArray } from '@/utils/helpers';
import { flattenDeep } from 'lodash';

const calendarService = new CalendarService();

export const state = {
  calendarOptions: {},
  calendars: [] as Calendar[],
  calendarIds: [] as number[],
  events: [] as CalendarEvent[],
  occurrences: [] as CalendarOccurrence[],
  currentOccurrence: {} as CalendarOccurrence,
  selectedCalendars: [],
  loading: false,
  error: {}
};

export const mutations = {
  // Calendar mutations
  SET_CALENDAR_IDS: (state: any, payload: any) => {
    state.calendarIds = payload;
  },
  SET_SELECTED_CALENDAR: (state: any, payload: any) => {
    state.selectedCalendars = payload;
  },
  ADD_CALENDAR(state: any, payload: Calendar) {
    state.calendars.push(payload);
  },

  SET_CALENDAR(state: any, payload: Calendar[]) {
    state.calendars = payload;
  },
  SET_OPTIONS_CALENDAR(state: any, payload: object) {
    state.calendarOptions = payload;
  },

  UPDATE_CALENDAR(state: any, payload: Calendar) {
    const index = state.calendars.findIndex(
      (calendar: Calendar) => calendar.id === payload.id
    );
    state.calendars.splice(index, 1, payload);
  },

  REMOVE_CALENDAR(state: any, payload: number) {
    const index = state.calendars.findIndex(
      (calendar: Calendar) => calendar.id === payload
    );
    state.calendars.splice(index, 1);
  },

  // Calendar Category mutations
  ADD_CALENDAR_EVENT(state: any, payload: CalendarEvent) {
    state.events.push(payload);
  },

  SET_CALENDAR_EVENTS(state: any, payload: CalendarEvent[]) {
    state.events = payload;
  },

  UPDATE_CALENDAR_EVENT(state: any, payload: CalendarEvent) {
    const index = state.events.findIndex(
      (category: CalendarEvent) => category.id === payload.id
    );
    state.events.splice(index, 1, payload);
  },

  REMOVE_CALENDAR_EVENT(state: any, payload: number) {
    const index = state.events.findIndex(
      (category: CalendarEvent) => category.id === payload
    );
    state.events.splice(index, 1);
  },

  // occurrences
  ADD_CALENDAR_OCCURRENCE(state: any, payload: CalendarOccurrence) {
    state.occurrences.push(payload);
  },
  SET_CALENDAR_OCCURRENCES(state: any, payload: CalendarOccurrence[]) {
    state.occurrences = payload;
  },
  SET_CALENDAR_OCCURRENCE(state: any, payload: CalendarOccurrence) {
    state.currentOccurrence = payload;
  },

  UPDATE_CALENDAR_OCCURRENCE(state: any, payload: CalendarOccurrence) {
    const index = state.occurrences.findIndex(
      (occurrence: CalendarOccurrence) =>
        occurrence.original_start === payload.original_start
    );
    state.occurrences.splice(index, 1, payload);
  },
  REMOVE_CALENDAR_OCCURRENCE(state: any, payload: CalendarOccurrence) {
    const index = state.occurrences.findIndex(
      (occurrence: CalendarOccurrence) =>
        occurrence.original_start === payload.original_start
    );
    state.occurrences.splice(index, 1);
  },

  SET_LOADING(state: any, payload: boolean) {
    state.loading = payload;
  },

  SET_ERROR(state: any, error: any) {
    errorCatch(state, error);
  },

  CLEAR_ERROR(state: any) {
    state.error = {};
  }
};

export const actions = {
  clearError: ({ commit }: any) => commit('CLEAR_ERROR'),
  // Calendar actions
  createCalendar: async ({ commit, dispatch }: any, payload: Calendar) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      calendarService
        .createCalendar(payload)
        .then(calendar => {
          commit('ADD_CALENDAR', calendar);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchCalendars: async (
    { commit, dispatch }: any,
    payload: { event: string; }
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    return await new Promise((resolve, reject) => {
      calendarService
        .fetchCalendars(payload.event)
        .then(calendars => {
          commit('SET_CALENDAR', calendars);
          commit(
            'SET_CALENDAR_IDS',
            calendars.map((calendar: Calendar) => calendar.id)
          );
          resolve(calendars);
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchCalendar: async ({ commit, state, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      calendarService
        .fetchCalendar({ calendarId: payload })
        .then(calendar => {
          // const calendars = [...state.calendars];
          const index = state.calendars.findIndex(
            (s: Calendar) => s.id === payload
          );
          if (index === -1) {
            commit('ADD_CALENDAR', calendar);
          } else {
            commit('UPDATE_CALENDAR', calendar);
          }
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  updateCalendar: async ({ commit, dispatch }: any, payload: Calendar) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      calendarService
        .updateCalendar(payload.id, payload)
        .then(calendar => {
          commit('UPDATE_CALENDAR', calendar);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  deleteCalendar: async ({ commit, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      calendarService
        .deleteCalendar(payload)
        .then(() => {
          commit('REMOVE_CALENDAR', payload);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  optionsCalendar: async ({ commit, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      calendarService
        .optionsCalendar(payload)
        .then(res => {
          commit('SET_OPTIONS_CALENDAR', res.actions.PUT);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  optionsCalendars: async ({ commit, dispatch }: any) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      calendarService
        .optionsCalendars()
        .then(res => {
          commit('SET_OPTIONS_CALENDAR', res.actions.POST);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  sendCalendarOccurrenceInvite: async (
    { commit, dispatch }: any,
    payload: any
  ) => {
    return await new Promise((resolve, reject) => {
      calendarService
        .sendCalendarOccurrenceInvite(
          {
            calendarId: payload.calendarId,
            eventId: payload.eventId
          },
          {
            year: payload.year,
            month: payload.month,
            day: payload.day,
            hour: payload.hour,
            minute: payload.minute,
            second: payload.second
          }
        )
        .then(occurrence => {
          resolve(occurrence);
        })
        .catch(err => {
          reject();
        })
        .finally(() => { });
    });
  },

  getCalendarOccurrenceInvite: async (
    { commit, dispatch }: any,
    payload: any
  ) => {
    return await new Promise((resolve, reject) => {
      calendarService
        .getCalendarOccurrenceInvite({
          calendarId: payload.calendarId,
          eventId: payload.eventId,
          occurrenceId: payload.occurrenceId,
          token: payload.token,
          rsvp: payload.rsvp
        })
        .then((result: Partial<CalendarOccurrence & { rsvp: number; }>) => {
          console.log('fetch calendar occurrence invite');
          resolve(result);
        })
        .catch((err: Error) => {
          reject(err);
        });
    });
  },

  fetchCalendarOccurrence: async ({ commit, dispatch }: any, payload: any) => {
    console.log({ payload });

    dispatch('clearError');
    commit('SET_LOADING', true);

    return await new Promise((resolve, reject) => {
      calendarService
        .fetchCalendarOccurrence(
          {
            calendarId: payload.calendarId,
            eventId: payload.eventId
          },
          {
            year: payload.year,
            month: payload.month,
            day: payload.day,
            hour: payload.hour,
            minute: payload.minute,
            second: payload.second
          }
        )
        .then(occurrence => {
          commit('SET_CALENDAR_OCCURRENCE', occurrence);

          resolve(occurrence);
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchCalendarOccurrences: async ({ commit, dispatch }: any, payload: any) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    return await new Promise((resolve, reject) => {
      calendarService
        .fetchCalendarOccurrences(
          {
            calendarId: payload.calendarId
          },
          {
            start: payload.start,
            end: payload.end
          }
        )
        .then(occurrences => {
          occurrences.map((occurrence: CalendarOccurrence | any) => {
            console.log('START CAL', occurrence.start_cal);
          });

          commit('SET_CALENDAR_OCCURRENCES', occurrences);

          resolve(occurrences);
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },
  sendCalendarEventInvite: async (
    { commit, dispatch }: any,
    payload: { calendarId: number; eventId: number; }
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      calendarService
        .sendCalendarEventInvite({
          calendarId: payload.calendarId,
          eventId: payload.eventId
        })
        .then(event => {
          console.log('sent calendar invite');
          console.log('INVITE RESPONSE: ', event);
          // commit('ADD_CALENDAR_EVENT', event);

          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },
  getCalendarEventInvite: async ({ commit, dispatch }: any, payload: any) => {
    return await new Promise((resolve, reject) => {
      calendarService
        .getCalendarEventInvite({
          calendarId: payload.calendarId,
          eventId: payload.eventId,
          token: payload.token,
          rsvp: payload.rsvp
        })
        .then((result: Partial<CalendarOccurrence & { rsvp: number; }>) => {
          console.log('fetch calendar event invite');
          resolve(result);
        })
        .catch((err: Error) => {
          reject(err);
        });
    });
  },

  updateCalendarOccurrence: async ({ commit, dispatch }: any, payload: any) => {
    console.log({ payload });

    dispatch('clearError');
    commit('SET_LOADING', true);

    return await new Promise((resolve, reject) => {
      calendarService
        .updateCalendarOccurrence(
          {
            calendarId: payload.calendarId,
            eventId: payload.eventId
          },
          {
            year: payload.year,
            month: payload.month,
            day: payload.day,
            hour: payload.hour,
            minute: payload.minute,
            second: payload.second
          },
          payload.data
        )
        .then((occurrence: CalendarOccurrence | any) => {
          const index = state.occurrences.findIndex(
            (s: CalendarOccurrence) =>
              s.original_start === payload.data.original_start
          );
          if (index === -1) {
            commit('ADD_CALENDAR_OCCURRENCE', occurrence);
          } else {
            commit('UPDATE_CALENDAR_OCCURRENCE', occurrence);
          }

          resolve(occurrence);
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },
  deleteCalendarOccurrence: async ({ commit, dispatch }: any, payload: any) => {
    console.log({ payload });

    dispatch('clearError');
    commit('SET_LOADING', true);

    return await new Promise((resolve, reject) => {
      calendarService
        .deleteCalendarOccurrence(
          {
            calendarId: payload.calendarId,
            eventId: payload.eventId
          },
          {
            year: payload.year,
            month: payload.month,
            day: payload.day,
            hour: payload.hour,
            minute: payload.minute,
            second: payload.second
          }
        )
        .then((occurrence: CalendarOccurrence | any) => {
          commit('REMOVE_CALENDAR_OCCURRENCE', occurrence);

          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  getOccurrences: async (
    { commit, dispatch }: any,
    payload: { list: number[]; start: string; end: string; }
  ): Promise<any[]> => {
    console.log({ list: payload.list });

    let data: any[] = [];
    if (!isArray(payload.list)) return [];
    if (payload.list.length) {
      data = payload.list.map(calendar =>
        dispatch('fetchCalendarOccurrences', {
          calendarId: calendar,
          start: payload.start,
          end: payload.end
        })
      );
    } else if (!state.calendarIds.length) {
      // return [];
    } else {
      console.log('No Payload');
      await dispatch('fetchCalendars').then(() => {
        console.log('No Payload');

        data = state.calendarIds.map((calendar: any) =>
          dispatch('fetchCalendarOccurrences', {
            calendarId: calendar,
            start: payload.start,
            end: payload.end
          })
        );
      });
    }

    const occurrenceList = await Promise.all(data).then(array => {
      return flattenDeep(array);
    });

    commit('SET_CALENDAR_OCCURRENCES', occurrenceList);
    return occurrenceList;
  },

  createCalendarEvent: async (
    { commit, dispatch }: any,
    payload: { calendarId: number; data: CalendarEvent; }
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    return await new Promise((resolve, reject) => {
      calendarService
        .createCalendarEvent({
          calendarId: payload.calendarId,
          data: payload.data
        })
        .then(event => {
          console.log('created single calendar event');
          commit('ADD_CALENDAR_EVENT', event);

          resolve(event);
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchCalendarEvents: async ({ commit, dispatch }: any, payload: number) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      calendarService
        .fetchCalendarEvents(payload)
        .then(events => {
          console.log('fetched list of calendar event');

          commit('SET_CALENDAR_EVENTS', events);
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },

  fetchCalendarEvent: async (
    { commit, dispatch }: any,
    payload: { calendarId: number; eventId: number; }
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      calendarService
        .fetchCalendarEvent(payload.calendarId, payload.eventId)
        .then(event => {
          console.log('fetched single calendar event');

          const index = state.events.findIndex(
            (s: CalendarEvent) => s.id === payload.eventId
          );
          if (index === -1) {
            commit('ADD_CALENDAR_EVENT', event);
          } else {
            commit('UPDATE_CALENDAR_EVENT', event);
          }
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },
  updateCalendarEvent: async (
    { commit, dispatch }: any,
    payload: { calendarId: number; eventId: number; data: CalendarEvent; }
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    return await new Promise((resolve, reject) => {
      calendarService
        .updateCalendarEvent(payload.calendarId, payload.eventId, payload.data)
        .then((event: CalendarEvent) => {
          console.log('updated calendar event');

          const index = state.events.findIndex(
            (s: CalendarEvent) => s.id === payload.eventId
          );
          if (index === -1) {
            commit('ADD_CALENDAR_EVENT', event);
          } else {
            commit('UPDATE_CALENDAR_EVENT', event);
          }
          resolve(event);
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  },
  deleteCalendarEvent: async (
    { commit, dispatch }: any,
    payload: { calendarId: number; eventId: number; }
  ) => {
    dispatch('clearError');
    commit('SET_LOADING', true);

    await new Promise((resolve, reject) => {
      calendarService
        .deleteCalendarEvent(payload)
        .then(event => {
          console.log('updated calendar event');

          const index = state.events.findIndex(
            (s: CalendarEvent) => s.id === payload.eventId
          );
          if (index === -1) {
            commit('ADD_CALENDAR_EVENT', event);
          } else {
            commit('UPDATE_CALENDAR_EVENT', event);
          }
          resolve();
        })
        .catch(err => {
          commit('SET_ERROR', err);
          reject();
        })
        .finally(() => {
          commit('SET_LOADING', false);
        });
    });
  }
};

export const getters = {
  getCalendarById: (state: any) => (id: number) => {
    return state.calendars.find((calendar: Calendar) => calendar.id === id);
  },
  getCalendarIds: (state: any): number[] => {
    return state.calendars.map((calendar: Calendar) => calendar.id);
  },
  getCalendarEventById: (state: any) => (id: number) => {
    return state.events.find((event: CalendarEvent) => event.id === id);
  }
};
